import React, { useEffect, useState, useRef, useCallback } from 'react';

import { Carousel } from 'react-responsive-carousel';
import styled from 'styled-components';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import * as constNames from '../models/constant';

function MTImageCarouselComponent({
  imageList,
  galleryMode,
  floorPlanImg,
  isPhotoState,
  carouselState,
  setPhotoIndexCB,
  galleryModeCB,
  toggleCarouselStateCB,
}) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  const [isPortrait, setIsPortrait] = useState(window.innerWidth < window.innerHeight);

  const carouselRef = useRef(null);
  const prevSelectedItemRef = useRef();

  const handleOrientationChange = useCallback(() => {
    if (window.orientation === 0 || window.orientation === 180) {
      // Portrait mode
      setIsPortrait(true);
    } else if (Math.abs(window.orientation) === 90) {
      // Landscape mode
      setIsPortrait(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [handleOrientationChange]);

  const galleryModeCBChild = useCallback(() => {
    galleryModeCB(true);
  }, [galleryModeCB]);

  const setPhotoIndexCBChild = useCallback(
    (index) => {
      setPhotoIndexCB(index);
      setSelectedPhotoIndex(index);
    },
    [setPhotoIndexCB],
  );

  const toggleCarouselStateCBChild = useCallback(
    (state) => {
      toggleCarouselStateCB(state);
    },
    [toggleCarouselStateCB],
  );

  const calculateDelta = useCallback(
    (selectedIndex, previousIndex) => {
      // const isPortrait = Math.abs(window.orientation) === 90 ? true : false;
      const baseDelta = selectedIndex > previousIndex ? -280 : 280;

      let computedDelta = baseDelta;

      if (selectedIndex === 0) {
        computedDelta = isPortrait
          ? (window.innerWidth - 380) / 2 - 30
          : (window.innerWidth - (120 + 380)) / 2;
      } else {
        computedDelta =
          selectedIndex > previousIndex
            ? isPortrait
              ? (window.innerWidth - (30 + 380)) / 2 +
                ((previousIndex + 1) * baseDelta - (previousIndex + 1) * 48)
              : (window.innerWidth - (120 + 380)) / 2 +
                ((previousIndex + 1) * baseDelta - (previousIndex + 1) * 48)
            : isPortrait
            ? // ? -((selectedIndex + 1) * baseDelta + (window.screen.width - 380) + (baseDelta - 24))
              (window.innerWidth - (30 + 380)) / 2 -
              (selectedIndex * baseDelta + selectedIndex * 48)
            : (window.innerWidth - (120 + 380)) / 2 -
              (selectedIndex * baseDelta + selectedIndex * 48);
      }

      return computedDelta;
    },
    [isPortrait],
  );

  useEffect(() => {
    const ul = carouselRef.current?.querySelector('.slider');

    const isTabView = window.innerWidth > 767;

    if (ul && typeof prevSelectedItemRef.current !== 'undefined' && isTabView) {
      const delta = calculateDelta(
        selectedPhotoIndex,
        prevSelectedItemRef.current,
        window.innerWidth,
        window.innerHeight,
      );

      setTimeout(() => {
        ul.style.transform = `translate3d(${delta}px, 0, 0)`;
      }, 0);
    }

    if (
      typeof prevSelectedItemRef.current === 'undefined' &&
      !galleryMode &&
      isTabView &&
      carouselState === 'PHOTOS'
    ) {
      const initialTransform = (window.screen.width - (30 + 380)) / 2;

      setTimeout(() => {
        ul.style.transform = `translate3d(${initialTransform}px, 0, 0)`;
      }, 0);
    }

    prevSelectedItemRef.current = selectedPhotoIndex;
  }, [
    carouselState,
    galleryMode,
    selectedPhotoIndex,
    imageList.length,
    calculateDelta,
    isPortrait,
  ]);

  const toggleBodyScroll = useCallback((enable) => {
    if (enable) {
      document.body.style.overflow = 'auto';
    } else {
      document.body.style.overflow = 'hidden';
    }
  }, []);

  useEffect(() => {
    const carouselElement = document.querySelector('.slider-wrapper');

    function handleTouchStart() {
      toggleBodyScroll(false);
    }

    function handleTouchEnd() {
      toggleBodyScroll(true);
    }

    carouselElement?.addEventListener('touchstart', handleTouchStart);

    carouselElement?.addEventListener('touchend', handleTouchEnd);

    return () => {
      carouselElement?.removeEventListener('touchstart', handleTouchStart);

      carouselElement?.removeEventListener('touchend', handleTouchEnd);
    };
  }, [toggleBodyScroll]);

  return (
    imageList?.length > 0 &&
    floorPlanImg?.length > 0 && (
      <>
        <CarouselWrapper ref={carouselRef} carouselState={carouselState}>
          <Carousel
            showArrows={false}
            showStatus={false}
            showThumbs={false}
            showIndicators={isPhotoState ? imageList.length > 1 : floorPlanImg?.length > 1}
            selectedItem={selectedPhotoIndex}
            transitionTime={500}
            preventMovementUntilSwipeScrollTolerance={true}
            swipeScrollTolerance={100}
            swipeable
            renderIndicator={(clickHandler, isSelected) => {
              return (
                <span
                  onClick={clickHandler}
                  className={isSelected ? 'ellipsis-selected' : 'ellipsis-unselected'}
                />
              );
            }}
            onChange={(index) => {
              setPhotoIndexCBChild(index);
            }}
            onClickItem={galleryModeCBChild}
          >
            {isPhotoState
              ? imageList?.map((url, i) => <img key={i} src={url} />)
              : floorPlanImg.map((url, i) => (
                  <img key={i} src={url} className="floor-image-wrapper" />
                ))}
          </Carousel>
        </CarouselWrapper>

        <ImageTabsWrapper>
          <button
            className={`buttonchildTab childTab1 ${
              isPhotoState ? 'buttonchildTabSecondary' : 'buttonchildTabPrimary'
            }`}
            onClick={() => toggleCarouselStateCBChild(constNames.FLOOR_PLAN)}
            title="Floor Plan"
          >
            Floor Plan
          </button>
          <button
            className={`buttonchildTab childTab2 ${
              isPhotoState ? 'buttonchildTabPrimary' : 'buttonchildTabSecondary'
            }`}
            onClick={() => toggleCarouselStateCBChild(constNames.PHOTOS)}
            title="Photos"
          >
            Photos
          </button>
        </ImageTabsWrapper>
      </>
    )
  );
}

const CarouselWrapper = styled.div`
  display: grid;
  width: 100%;
  justify-content: center;
  @media ${constNames.DEVICE_MEDIA.MOBILE_MAX} and (orientation: ${constNames.DEVICE_ORIENTATION
      .LANDSCAPE}) {
    width: auto;
  }
  @media (orientation: ${constNames.DEVICE_ORIENTATION.LANDSCAPE}) and (orientation: ${constNames
      .DEVICE_ORIENTATION.POTRAIT}) {
    width: 380px;
  }

  @media ${constNames.DEVICE_MEDIA.TABLET_MIN} {
    width: calc(100vw - 30px);
    overflow: hidden;
  }
  @media ${constNames.DEVICE_MEDIA.TABLET_MIN} and (orientation: ${constNames.DEVICE_ORIENTATION
      .LANDSCAPE}) {
    width: 100%;
    margin: 0 auto;
  }
  @media ${constNames.DEVICE_MEDIA.TABLET_MIN} and (orientation: ${constNames.DEVICE_ORIENTATION
      .LANDSCAPE}),
    ${constNames.DEVICE_MEDIA.TABLET_MIN} and (orientation: ${constNames.DEVICE_ORIENTATION
      .POTRAIT}) {
    order: 0;
  }
  .carousel-root {
    @media ${constNames.DEVICE_MEDIA.TABLET_MIN} {
      width: 100%;
      margin-top: 25px;
    }
    @media ${constNames.DEVICE_MEDIA.TABLET_MIN} and (orientation: ${constNames.DEVICE_ORIENTATION
        .LANDSCAPE}) {
      width: calc(100vw - 120px);
    }
  }
  .slider {
    transform: ${(props) =>
      props.carouselState === 'FLOOR_PLAN' ? 'translate3d(0px, 0px, 0px) !important' : ''};
  }
  & .carousel.carousel-slider {
    width: calc(100vw - 30px) !important;
    @media (orientation: ${constNames.DEVICE_ORIENTATION.LANDSCAPE}) and ${constNames.DEVICE_MEDIA
        .MOBILE_MAX} {
      width: auto;
    }
    @media ${constNames.DEVICE_MEDIA.TABLET_MIN} and (orientation: ${constNames.DEVICE_ORIENTATION
        .LANDSCAPE}) {
      width: calc(100vw - 120px) !important;
    }
    @media ${constNames.DEVICE_MEDIA.TABLET_MIN} {
      height: 300px;
    }
    & .slider-wrapper {
      @media (orientation: ${constNames.DEVICE_ORIENTATION.LANDSCAPE}) and ${constNames.DEVICE_MEDIA
          .MOBILE_MAX} {
        width: 340px;
      }
    }
  }

  & .carousel .slider-wrapper.axis-horizontal .slider {
    align-items: center;
    @media (orientation: ${constNames.DEVICE_ORIENTATION.LANDSCAPE}) and ${constNames.DEVICE_MEDIA
        .MOBILE_MAX} {
      width: 340px;
    }
    @media ${constNames.DEVICE_MEDIA.TABLET_MIN} {
      gap: 48px;
    }
  }

  & .carousel .slider-wrapper.axis-horizontal .slider .slide.selected {
    @media ${constNames.DEVICE_MEDIA.MOBILE_MAX} {
      margin-right: 30px;
    }
    @media (orientation: ${constNames.DEVICE_ORIENTATION.LANDSCAPE}) and ${constNames.DEVICE_MEDIA
        .MOBILE_MAX} {
      margin-right: 100px;
    }
    @media ${constNames.DEVICE_MEDIA.TABLET_MIN} {
      min-width: 380px !important;
      min-height: 300px !important;
    }
  }
  & .carousel .slider-wrapper.axis-horizontal .slider .slide {
    display: grid;
    align-items: center;
    justify-content: center;
    gap: 15px;
    height: 230px;

    @media ${constNames.DEVICE_MEDIA.TABLET_MIN} {
      min-width: 280px;
      min-height: 220px;
      max-width: ${(props) => (props.carouselState === 'FLOOR_PLAN' ? '380px' : '')};
      margin: ${(props) => (props.carouselState === 'FLOOR_PLAN' ? '0 auto !important' : '')};
      height: ${(props) => (props.carouselState === 'FLOOR_PLAN' ? 'auto' : 'auto')};
      &.selected {
        img {
          height: auto;
        }
      }
    }
  }
  & .carousel .slide img {
    object-fit: cover;
    border-radius: 8px;
    border: solid 1px #ddd;
    @media ${constNames.DEVICE_MEDIA.MOBILE_MAX} {
      width: 340px;
      // height: 230px;
    }
    @media ${constNames.DEVICE_MEDIA.TABLET_MIN} {
      height: ${(props) => (props.carouselState === 'FLOOR_PLAN' ? 'auto' : 'auto')};
    }
  }
  & ul {
    z-index: 1;
  }
  & .carousel .control-dots {
    z-index: 2 !important;
    & span {
      margin-right: 25px;
      display: inline-block;
      border-radius: 50%;
      cursor: pointer;
    }
  }
  & .ellipsis-unselected {
    height: 12px;
    width: 12px;
    border: 1px solid #dfe6e5;
    margin-bottom: 2px;
    border: 1px solid rgb(255 255 255);
    background-color: rgb(3, 40, 66);
  }
  & .ellipsis-selected {
    height: 16px;
    width: 16px;
    border: 1px solid #fff;
    background-color: #003f2d;
  }
`;

const ImageTabsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin:20px 0;
  & .buttonchildTab {
    padding: 3px 10px;
    display: flex;
    padding: 3.232px 10px 4.768px 10px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    border: none;
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; 
    letter-spacing: 0.06px;
  }
  & .buttonchildTabPrimary {
    color: rgba(26, 26, 26, 0.46)
    background-color: rgba(26, 26, 26, 0.12);
  }
  & .buttonchildTabPrimary {
    color: #fff;
    background-color: #032842;
  }
  & .buttonchildTabSecondary{
    color: rgba(26, 26, 26, 0.60);
    background: rgba(26, 26, 26, 0.12);
  }
  & .childTab1 {
    justify-self: end;
  }
  & .childTab2 {
    justify-self: start;
  }
`;

export default MTImageCarouselComponent;
